/**
 * ProfilePanelCyberpunk
 * 
 * Cyberpunk-themed profile modal with neon borders, split layout,
 * and futuristic visual styling for the "Phishing au Ministère" scenario.
 */

import { useState } from 'react';
import { Item, Skill, Achievement, CharacterPortrait } from '../types';
import { ItemCard } from './item-card';
import { SkillCard } from './skill-card';
import styles from './profile-panel-cyberpunk.module.css';

type Section = 'skills' | 'inventory' | 'achievements' | 'characters';

interface ProfilePanelCyberpunkProps {
  onClose: () => void;
  inventory: string[];
  items: Item[];
  unlockedSkills: string[];
  skills: Skill[];
  achievements: Achievement[];
  unlockedAchievements: string[];
  characters?: CharacterPortrait[];
  playerName?: string;
}

export function ProfilePanelCyberpunk({
  onClose,
  inventory = [],
  items,
  unlockedSkills,
  skills,
  achievements = [],
  unlockedAchievements = [],
  characters = [],
  playerName = 'Agent',
}: ProfilePanelCyberpunkProps): JSX.Element {
  const [activeSection, setActiveSection] = useState<Section>('skills');
  const inventoryItems = inventory.map(itemId => items.find(item => item.id === itemId)).filter(Boolean) as Item[];

  const sections: { key: Section; label: string; count: string }[] = [
    { key: 'skills', label: 'Compétences', count: `${unlockedSkills.length}/${skills.length}` },
    { key: 'inventory', label: 'Inventaire', count: `${inventory.length}` },
    { key: 'achievements', label: 'Succès', count: `${unlockedAchievements.length}/${achievements.length}` },
    ...(characters.length > 0 ? [{ key: 'characters' as Section, label: 'Personnages', count: `${characters.length}` }] : []),
  ];

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-title-cyber"
    >
      <div className={styles.frame} onClick={(e) => e.stopPropagation()}>
        {/* Corner decorations */}
        <div className={`${styles.corner} ${styles.cornerTL}`} />
        <div className={`${styles.corner} ${styles.cornerTR}`} />
        <div className={`${styles.corner} ${styles.cornerBL}`} />
        <div className={`${styles.corner} ${styles.cornerBR}`} />

        {/* Title bar */}
        <div className={styles.titleBar}>
          <h2 id="profile-title-cyber" className={styles.title}>PROFIL</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close profile"
          >✕</button>
        </div>

        {/* Main content: split layout */}
        <div className={styles.body}>
          {/* Left panel: player info */}
          <div className={styles.leftPanel}>
            <div className={styles.avatarSection}>
              <div className={styles.avatar}>
                <span className={styles.avatarIcon}>👤</span>
              </div>
              <div className={styles.playerInfo}>
                <div className={styles.playerName}>{playerName}</div>
                <div className={styles.playerRank}>
                  <span className={styles.rankBadge}>🛡️</span>
                  Sentinel
                </div>
              </div>
            </div>

            <div className={styles.statsDivider} />

            {/* Navigation sections */}
            <nav className={styles.sectionNav}>
              {sections.map((section) => (
                <button
                  key={section.key}
                  className={`${styles.sectionBtn} ${activeSection === section.key ? styles.sectionBtnActive : ''}`}
                  onClick={() => setActiveSection(section.key)}
                  aria-selected={activeSection === section.key}
                >
                  <span className={styles.sectionLabel}>{section.label}</span>
                  <span className={styles.sectionCount}>{section.count}</span>
                  {activeSection === section.key && (
                    <span className={styles.sectionArrow}>▶</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Right panel: content */}
          <div className={styles.rightPanel}>
            {/* Skills section */}
            {activeSection === 'skills' && (
              <div className={styles.contentSection}>
                {skills.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🔒</div>
                    <p>Aucune compétence disponible</p>
                    <p className={styles.emptyHint}>Progressez dans l&apos;histoire pour débloquer des compétences</p>
                  </div>
                ) : (
                  <div className={styles.skillList} role="list" aria-label="Compétences">
                    {skills.map((skill) => {
                      const isUnlocked = unlockedSkills.includes(skill.id);
                      return (
                        <div key={skill.id} role="listitem">
                          <SkillCard skill={skill} isLocked={!isUnlocked} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Inventory section */}
            {activeSection === 'inventory' && (
              <div className={styles.contentSection}>
                {inventoryItems.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🎒</div>
                    <p>Inventaire vide</p>
                    <p className={styles.emptyHint}>Collectez des objets au fil de l&apos;aventure</p>
                  </div>
                ) : (
                  <div className={styles.itemGrid} role="list" aria-label="Inventaire">
                    {inventoryItems.map((item) => (
                      <div key={item.id} role="listitem">
                        <ItemCard item={item} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Achievements section */}
            {activeSection === 'achievements' && (
              <div className={styles.contentSection}>
                {achievements.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>⭐</div>
                    <p>Aucun succès disponible</p>
                  </div>
                ) : (
                  <div className={styles.achievementList} role="list" aria-label="Succès">
                    {achievements.map((achievement) => {
                      const isUnlocked = unlockedAchievements.includes(achievement.id);
                      return (
                        <div
                          key={achievement.id}
                          role="listitem"
                          className={`${styles.achievementCard} ${isUnlocked ? styles.achievementUnlocked : styles.achievementLocked}`}
                        >
                          <div className={styles.achievementIcon}>
                            {achievement.icon ? (
                              <img src={achievement.icon} alt="" />
                            ) : (
                              <span>{isUnlocked ? '⭐' : '🔒'}</span>
                            )}
                          </div>
                          <div className={styles.achievementInfo}>
                            <h3 className={styles.achievementName}>
                              {achievement.name}
                            </h3>
                            <p className={styles.achievementDesc}>
                              {achievement.description}
                            </p>
                          </div>
                          {isUnlocked && (
                            <div className={styles.achievementCheck}>✓</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Characters section */}
            {activeSection === 'characters' && (
              <div className={styles.contentSection}>
                {characters.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>👥</div>
                    <p>Aucun personnage rencontré</p>
                  </div>
                ) : (
                  <div className={styles.characterList} role="list" aria-label="Personnages">
                    {characters.map((character) => {
                      const confidence = character.confidence ?? 0;
                      return (
                        <div key={character.id} role="listitem" className={styles.characterCard}>
                          <div className={styles.characterAvatar}>
                            {character.icon ? (
                              <img src={character.icon} alt="" />
                            ) : (
                              <span>👤</span>
                            )}
                          </div>
                          <div className={styles.characterInfo}>
                            <div className={styles.characterName}>{character.name}</div>
                            <div className={styles.confidenceRow}>
                              <span className={styles.confidenceLabel}>Confiance</span>
                              <div className={styles.confidenceBar}>
                                <div
                                  className={styles.confidenceFill}
                                  style={{ width: `${confidence}%` }}
                                />
                              </div>
                              <span className={styles.confidenceValue}>{confidence}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
